import React from 'react';
import styled from 'styled-components';
import { Button, Modal } from '../../../../Components/uielements';
import {
  OutlineButton,
  ModalButtonWrapper,
} from '../../../../services/settings/appStyle/global.style';
import { IntlMessages } from '../../../../helper/langHelper';

const ResultModalWrapper = styled.div`
  display: grid;
  gap: 10px;
  padding: 10px 20px 30px;
  min-width: 800px;
`;

const HelperText = styled.p`
  font-size: 14px;
  color: #dad8d8;
  line-height: 1.6;
  font-weight: 400;
`;

const MessageContainer = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 16px;
  margin: 20px 0;
`;

const LinkContainer = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 16px;
  margin: 20px 0;
  position: relative;
`;

const MessageLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MessageDisplay = styled.textarea`
  width: 100%;
  min-height: 180px;
  max-height: 260px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #fff;
  background: transparent;
  resize: vertical;
  box-sizing: border-box;
  white-space: pre-wrap;
  line-height: 1.5;
  overflow: auto;

  &:focus {
    outline: none;
    border-color: #0071e3;
  }
`;

const LinkLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LinkTextArea = styled.textarea`
  width: 100%;
  min-height: 56px;
  max-height: 84px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #fff;
  background: transparent;
  resize: none;
  box-sizing: border-box;
  white-space: pre-wrap;
  overflow: hidden;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: #0071e3;
  }
`;

const CopyButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const InviteResultModal = ({ inviteData, onClose }) => {
  const messageTextAreaRef = React.useRef(null);
  const linkTextAreaRef = React.useRef(null);
  const [messageCopied, setMessageCopied] = React.useState(false);
  const [linkCopied, setLinkCopied] = React.useState(false);

  const copyText = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleCopyMessage = () => {
    if (messageTextAreaRef.current) {
      copyText(messageTextAreaRef.current.value, setMessageCopied);
    }
  };

  const handleCopyLink = () => {
    if (linkTextAreaRef.current) {
      copyText(linkTextAreaRef.current.value, setLinkCopied);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Invite Links Generated">
      <ResultModalWrapper>
        {inviteData.inviteMessage && (
          <MessageContainer>
            <MessageLabel>Full Invitation Message</MessageLabel>
            <MessageDisplay
              ref={messageTextAreaRef}
              readOnly
              value={inviteData.inviteMessage.replace(/\\n/g, '\n').replace(/\\t/g, '\t')}
            />
            <CopyButtonWrapper>
              <Button varient="normal" onClick={handleCopyMessage}>
                {messageCopied ? '✓ Copied!' : 'Copy Message'}
              </Button>
            </CopyButtonWrapper>
          </MessageContainer>
        )}

        {inviteData.link && (
          <LinkContainer>
            <LinkLabel>Invitation Link</LinkLabel>
            <LinkTextArea ref={linkTextAreaRef} readOnly value={inviteData.link} />
            <CopyButtonWrapper>
              <Button varient="normal" onClick={handleCopyLink}>
                {linkCopied ? '✓ Copied!' : 'Copy Link'}
              </Button>
            </CopyButtonWrapper>
          </LinkContainer>
        )}

        {inviteData.invitationCode && (
          <HelperText style={{ fontSize: '12px', opacity: 0.8 }}>
            <strong>Code:</strong> {inviteData.invitationCode}
            {inviteData.expiryDuration && (
              <>
                {' '}
                | <strong>Expires in:</strong> {inviteData.expiryDuration} hours
              </>
            )}
          </HelperText>
        )}

        <ModalButtonWrapper style={{ marginTop: '24px' }}>
          <OutlineButton onClick={onClose}>
            <IntlMessages id="modal.button.close" defaultMessage="Close" />
          </OutlineButton>
        </ModalButtonWrapper>
      </ResultModalWrapper>
    </Modal>
  );
};

export default InviteResultModal;
